import React, {useCallback, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import JsonFormsWrapper from '@/components/common/jsonForms/jsonFormsWrapper/JsonFormsWrapper';
import {Counter} from '@/components/atoms';
import LabelWithTooltip from '@/components/atoms/LabelWithTooltip/LabelWithTooltip';
import {
  easyOrderActivityJsonSchema,
  easyOrderActivityUiSchema,
  welcomeActivityAvailabilityJsonSchema,
  welcomeActivityAvailabilityUiSchema,
  welcomeActivityHasEndDateSchema,
  welcomeActivityHasEndUiSchema,
  welcomeActivityEndDateJsonSchema,
  welcomeActivityEndDateUiSchema
} from '@/services/schema/activity';
import styles from './EasyOrderActivitySetUp.module.scss';
import {isEasyOrderPhotoOrderModalDisplayLimitInvalid} from '@/utils/activityValidation';
import {
  blockDisplayOrderKeyDown,
  sanitizeNonNegativeIntegerInput
} from '@/utils/displayOrderValidation';

const EasyOrderActivitySetUp = ({
  activitySelection,
  formData,
  handleFormChange
}) => {
  const handleJsonChange = useCallback(
    (data) => {
      handleFormChange(data);
    },
    [handleFormChange]
  );

  const activitySetupUi = useMemo(() => easyOrderActivityUiSchema(), []);

  const startDateAvailabilityUi = useMemo(
    () =>
      welcomeActivityAvailabilityUiSchema(
        formData?.endDate || '',
        formData?.startDate || '',
        formData,
        'top-left'
      ),
    [formData]
  );

  const endDateUi = useMemo(
    () =>
      welcomeActivityEndDateUiSchema(
        formData?.startDate,
        formData?.endDate,
        'top-left'
      ),
    [formData?.startDate, formData?.endDate]
  );

  const [photoOrderLimitTouched, setPhotoOrderLimitTouched] = useState(false);

  const photoOrderLimitInvalid = isEasyOrderPhotoOrderModalDisplayLimitInvalid(
    formData?.photoOrderModalDisplayLimit
  );
  const showPhotoOrderLimitError =
    photoOrderLimitTouched && photoOrderLimitInvalid;

  const handlePhotoOrderLimitUpdate = useCallback(
    (nextValue) => {
      setPhotoOrderLimitTouched(true);
      handleFormChange({
        photoOrderModalDisplayLimit: sanitizeNonNegativeIntegerInput(nextValue)
      });
    },
    [handleFormChange]
  );

  const counterDisplayValue =
    formData?.photoOrderModalDisplayLimit === '' ||
    formData?.photoOrderModalDisplayLimit === null ||
    formData?.photoOrderModalDisplayLimit === undefined
      ? 0
      : formData.photoOrderModalDisplayLimit;

  return (
    <div
      className='flex w-full flex-col gap-8'
      data-testid='easy-order-activity-card'>
      <div className='flex flex-col gap-4'>
        <h3 className='text-base font-bold text-white'>Activity Setup</h3>
        <div className={styles.easyOrderSetupRow}>
          <div className={styles.activityTypeWrap}>{activitySelection}</div>
          <div className={styles.easyOrderFieldsWrap}>
            <JsonFormsWrapper
              jsonschema={easyOrderActivityJsonSchema}
              uischema={activitySetupUi}
              data={formData}
              onChange={handleJsonChange}
            />
          </div>
          <div
            className={styles.photoOrderLimitWrap}
            data-testid='photo-order-modal-display-limit-field'>
            <LabelWithTooltip
              className='text-[12px] text-white'
              label='Photo Order Modal Display Limit *'
            />
            <div onKeyDownCapture={blockDisplayOrderKeyDown}>
              <Counter
                feedbackType={showPhotoOrderLimitError ? 'error' : 'info'}
                isDark
                value={counterDisplayValue}
                onUpdate={handlePhotoOrderLimitUpdate}
                minValue={0}
                size='medium'
                className='w-20'
                style={{width: '76px'}}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='flex flex-col gap-4'>
        <h3 className='text-base font-bold text-white'>Availability</h3>
        <div className={styles.availabilityDatesRow}>
          <div className={styles.availabilityRowWrap}>
            <JsonFormsWrapper
              jsonschema={welcomeActivityAvailabilityJsonSchema}
              uischema={startDateAvailabilityUi}
              data={formData}
              onChange={handleJsonChange}
              formData={formData}
            />
          </div>
          <div className={styles.hasEndDateRow}>
            <JsonFormsWrapper
              jsonschema={welcomeActivityHasEndDateSchema}
              uischema={welcomeActivityHasEndUiSchema}
              data={formData}
              onChange={handleJsonChange}
              formData={formData}
            />
          </div>
          {formData?.hasEndDate && (
            <div className={styles.endDateRow}>
              <JsonFormsWrapper
                jsonschema={welcomeActivityEndDateJsonSchema}
                uischema={endDateUi}
                data={formData}
                onChange={handleJsonChange}
                formData={formData}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

EasyOrderActivitySetUp.propTypes = {
  activitySelection: PropTypes.node,
  formData: PropTypes.object.isRequired,
  handleFormChange: PropTypes.func.isRequired
};

export default React.memo(EasyOrderActivitySetUp);
